import { useCallback, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
	sendSignupSmsCode,
	verifySignupSmsCode,
} from "@/api/adapters/signup";
import { ApiError } from "@/api/error";
import { toast } from "@/app/store/toast.store";
import { Button } from "@/components/ui/Button";
import {
	PhoneVerificationCodeSection,
	usePhoneVerification,
	type PhoneVerificationLabels,
} from '@/features/auth/components/PhoneVerificationPanel';
import { SignupTermsConsent } from '@/features/auth/components/SignupTermsConsent';
import { useSignupTerms } from '@/shared/hooks/use-signup-terms';
import type { SignupFormValues } from '@/features/auth/schemas/signup.schema';
import type { SignupConsent } from '@/features/auth/types/signup-terms';

interface SignupFormProps {
	formMethods: UseFormReturn<SignupFormValues>;
	onSubmit: (
		data: SignupFormValues,
		phoneVerificationToken: string,
		consents: SignupConsent[],
	) => void;
	/** The invite token from the URL — bound into the SMS send-code call (BE requires it in the body). */
	token: string;
	isPending?: boolean;
}

export const SignupForm = ({
	formMethods,
	onSubmit,
	token,
	isPending = false,
}: SignupFormProps) => {
	const { t } = useTranslation(['auth', 'validation']);
	const {
		register,
		handleSubmit,
		watch,
		trigger,
		setValue,
		setError: setFieldError,
		clearErrors,
		formState: { errors, isValid },
	} = formMethods;

	const [phoneError, setPhoneError] = useState('');

	// ── US-SU-05: Signup Terms ────────────────────────────────────────────
	const {
		data: signupTerms = [],
		isLoading: termsLoading,
		isError: termsError,
	} = useSignupTerms(token);

	const agreedTermIds: number[] = watch('agreedTermIds') ?? [];

	const passwordValue = watch("password");
	const passwordConfirmValue = watch("passwordConfirm");
	const nameValue = watch("name");
	const phoneValue = watch("phone") ?? "";

	const phoneVerifyLabels: PhoneVerificationLabels = useMemo(
		() => ({
			sendCode: t('auth:signup.actions.sendCode'),
			resend: t('auth:signup.actions.resend'),
			codeLabel: t('auth:signup.fields.verifyCode'),
			verify: t('auth:signup.actions.verify'),
			verified: `✓ ${t('auth:signup.messages.verified')}`,
			verifiedTimer: t('auth:signup.messages.verifiedTimer'),
			codeInvalid: t('auth:signup.errors.code.mismatch'),
			codeExpired: t('auth:signup.errors.code.expired'),
			alreadyVerified: t('auth:signup.errors.code.alreadyVerified'),
			sessionExpired: t('auth:signup.errors.code.expired'),
			serverError: t('auth:signup.errors.server.generic'),
		}),
		[t],
	);

	// SMS-specific errors stay form-local — they're tied to the phone RHF field.
	// The final completeSignup error switch lives in the page (centralized).
	const handleSendError = useCallback(
		(err: unknown) => {
			if (!(err instanceof ApiError)) {
				toast.error(t('auth:signup.errors.server.generic'));
				return;
			}
			switch (err.code) {
				case 'PHONE_INVALID': {
					const msg = t('auth:signup.errors.phone.invalid');
					setPhoneError(msg);
					setFieldError('phone', { message: msg });
					break;
				}
				// Q63 (anti-enumeration): sign-up must stay vague — never reveal
				// whether the number is already in use or doesn't match the
				// invitation. Both map to the same message. (SHCT-340)
				case 'PHONE_DUPLICATE':
				case 'SIGNUP_PHONE_MISMATCH': {
					const msg = t('auth:signup.errors.phone.duplicate');
					setPhoneError(msg);
					setFieldError('phone', { message: msg });
					break;
				}
				case 'SMS_SEND_FAILED':
					toast.error(t('auth:signup.errors.server.sendFailed'));
					break;
				case 'SIGNUP_SMS_RATE_LIMIT': {
					const retryAfter = Number(err.meta?.retryAfterSeconds ?? 60);
					toast.error(
						t('auth:signup.errors.server.tooMany', { seconds: retryAfter }),
					);
					break;
				}
				default:
					toast.error(t('auth:signup.errors.server.generic'));
			}
		},
		[t, setFieldError],
	);

	const handleVerifyError = useCallback(
		(err: unknown) => {
			if (!(err instanceof ApiError)) {
				toast.error(phoneVerifyLabels.serverError);
				return;
			}
			switch (err.code) {
				case 'OTP_INVALID':
					toast.error(phoneVerifyLabels.codeInvalid);
					break;
				case 'OTP_EXPIRED':
					toast.error(phoneVerifyLabels.codeExpired);
					break;
				case 'SIGNUP_PHONE_UNVERIFIED':
					toast.error(phoneVerifyLabels.sessionExpired);
					break;
				case 'UNAUTHENTICATED':
					break;
				default:
					toast.error(phoneVerifyLabels.serverError);
			}
		},
		[phoneVerifyLabels],
	);

	const phoneVerify = usePhoneVerification({
		phone: phoneValue,
		// SHCT-387: Signup has NO resend cooldown / "재전송" relabel — PRD 01 (element 7 + §6),
		// the medical signup prototype, and clarification Q53 only require the OTP-validity timer.
		// (The 60s resend cooldown is a My Profile-only spec, PRD 09 §3.3.) cooldownSec:0 keeps the
		// send button as "인증번호 받기". A real BE SIGNUP_SMS_RATE_LIMIT still disables it briefly.
		cooldownSec: 0,
		// Bind the invite token so the real BE send-code call carries it (mock ignores the 2nd arg).
		onSendCode: (phone) => sendSignupSmsCode(phone, token),
		onVerifyCode: verifySignupSmsCode,
		onVerified: () => {
			// Hook owns the token; we read it from `phoneVerify.token` at submit.
		},
		onSendError: handleSendError,
		onSendSuccess: () => toast.success(t('auth:signup.messages.sent')),
		onVerifyError: handleVerifyError,
		labels: phoneVerifyLabels,
	});

	const canSubmit =
		isValid &&
		phoneVerify.isVerified &&
		!isPending &&
		passwordValue &&
		passwordValue.length >= 8 &&
		passwordValue === passwordConfirmValue &&
		nameValue &&
		nameValue.trim().length > 0 &&
		// US-SU-05: all required terms must be agreed
		!termsLoading &&
		!termsError &&
		signupTerms.every(
			(term) => !term.required || agreedTermIds.includes(term.termVersionId),
		);

	const handleSendClick = useCallback(async () => {
		const valid = await trigger('phone');
		if (!valid) return;
		setPhoneError('');
		await phoneVerify.send();
	}, [trigger, phoneVerify]);

	const handleFormSubmit = handleSubmit(async (data) => {
		if (phoneVerify.token === null) {
			toast.error(
				t('auth:signup.errors.invitation.mobileVerificationRequired'),
			);
			return;
		}
		// US-SU-05: build consent decisions for every displayed term
		const consents: SignupConsent[] = signupTerms.map((term) => ({
			termVersionId: term.termVersionId,
			agreed: agreedTermIds.includes(term.termVersionId),
		}));
		await onSubmit(data, phoneVerify.token, consents);
	});

	const phoneFieldError =
		phoneError || (errors.phone?.message ? t(errors.phone.message) : '');

	return (
		<form className='auth-form' onSubmit={handleFormSubmit} noValidate>
			{/* Email (readonly) */}
			<div className='form-field'>
				<label className='form-field__label'>
					{t('auth:signup.fields.email')}
				</label>
				<input
					type='email'
					className='form-field__input'
					readOnly
					{...register('email')}
				/>
				<p className='form-field__hint'>{t('auth:signup.fields.emailHint')}</p>
			</div>

			{/* Password */}
			<div className='form-field'>
				<label className='form-field__label'>
					{t('auth:signup.fields.password')}
					<span className='required'> *</span>
				</label>
				<input
					type='password'
					className={`form-field__input${errors.password ? ' form-field__input--error' : ''}`}
					placeholder={t('auth:signup.fields.passwordPlaceholder')}
					{...register('password')}
				/>
				{errors.password && (
					<p className='form-field__error'>{t(errors.password.message!)}</p>
				)}
			</div>

			{/* Password Confirm */}
			<div className='form-field'>
				<label className='form-field__label'>
					{t('auth:signup.fields.passwordConfirm')}
					<span className='required'> *</span>
				</label>
				<input
					type='password'
					className={`form-field__input${errors.passwordConfirm ? ' form-field__input--error' : ''}`}
					{...register('passwordConfirm')}
				/>
				{errors.passwordConfirm && (
					<p className='form-field__error'>
						{t(errors.passwordConfirm.message!)}
					</p>
				)}
			</div>

			{/* Name */}
			<div className='form-field'>
				<label className='form-field__label'>
					{t('auth:signup.fields.name')}
					<span className='required'> *</span>
				</label>
				<input
					type='text'
					className={`form-field__input${errors.name ? ' form-field__input--error' : ''}`}
					{...register('name')}
				/>
				{errors.name && (
					<p className='form-field__error'>{t(errors.name.message!)}</p>
				)}
			</div>

			{/* Phone + Send Code */}
			<div className='form-field'>
				<label className='form-field__label'>
					{t('auth:signup.fields.phone')}
					<span className='required'> *</span>
				</label>
				<div className='inline-field'>
					<input
						type='tel'
						className={`form-field__input${phoneFieldError ? ' form-field__input--error' : ''}`}
						placeholder='01000000000'
						{...register('phone', {
							// PRD 01 §220 / [공통] 00 §2.1 — 입력부터 하이픈이 없어야 한다. Strip every
							// non-digit as it is typed and stop at 11, so a pasted `010-1234-5678`
							// becomes the canonical value instead of being refused at submit.
							//
							// Deliberately NOT a `maxLength` on the element: the browser applies that
							// to the RAW string before this handler runs, so `010-1234-5678` would be
							// cut to 11 characters — `010-1234-56` — and lose two digits to the
							// separators. The slice below is the only cap.
							onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
								setValue('phone', e.target.value.replace(/\D/g, '').slice(0, 11));
								setPhoneError('');
								clearErrors('phone');
							},
						})}
					/>
					<Button
						type='button'
						variant='secondary'
						className='btn-sm'
						onClick={handleSendClick}
						disabled={!phoneVerify.canSend || phoneValue.trim() === ''}
					>
						{t('auth:signup.actions.sendCode')}
					</Button>
				</div>
				{phoneFieldError && (
					<p className='form-field__error'>{phoneFieldError}</p>
				)}
			</div>

			{/* SMS Code section (shared component; renders when session active) */}
			<PhoneVerificationCodeSection
				controller={phoneVerify}
				inputId='signup-verify-code'
			/>

			{/* Institution (readonly) */}
			<div className='form-field'>
				<label className='form-field__label'>
					{t('auth:signup.fields.institution')}
				</label>
				<input
					type='text'
					className='form-field__input'
					readOnly
					{...register('institution')}
				/>
			</div>

			{/* Department (readonly) */}
			<div className='form-field'>
				<label className='form-field__label'>
					{t('auth:signup.fields.department')}
				</label>
				<input
					type='text'
					className='form-field__input'
					readOnly
					{...register('department')}
				/>
			</div>

			{/* Role (readonly) */}
			<div className='form-field'>
				<label className='form-field__label'>
					{t('auth:signup.fields.role')}
				</label>
				<input
					type='text'
					className='form-field__input'
					readOnly
					{...register('role')}
				/>
			</div>

			{/* US-SU-05: Terms consent — checkboxes + agree-all + [보기] modal.
			    Gates the submit button (required terms must all be agreed). */}
			<SignupTermsConsent
				terms={signupTerms}
				loading={termsLoading}
				error={termsError}
				agreedIds={agreedTermIds}
				onChange={(next) =>
					setValue('agreedTermIds', next, { shouldValidate: true })
				}
				disabled={isPending}
			/>

			{/* Submit */}
			<Button
				type='submit'
				variant='primary'
				className='w-full h-11 text-[var(--font-size-base)]'
				disabled={!canSubmit}
			>
				{isPending ? (
					<span className='spin' aria-hidden='true' />
				) : (
					t('auth:signup.actions.submit')
				)}
			</Button>
		</form>
	);
};
